import * as Yup from 'yup';
import { useFormik, FormikProvider } from "formik";
import { usePaymentForm } from "./customHook/usePaymentForm";
import { PaymentInfo } from "../../../../../../../types/payment.type";
import InputFormik from "../../../../../../../common/components/form/InputFormik";
import ComboBoxFormik from "../../../../../../../common/components/form/ComboBoxFormik";
import InputMoney from "../../../../../../../common/components/form/CurrencyInputFormik";
import ButtonWithLoader from "../../../../../../../common/components/ui/loading/ButtonLoading";
import { InstallmentOption, truncateDate, useCalculateInstallmentOptions } from '../../../../../helpers/calculationUtils';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useToastContext } from '../../../../../../../common/components/ui/toast/toast.context';
import SlideToggle from '../../../../../../../common/components/ui/toggleSwitch/SlideToggle';



type FormPropsEditPayments = {
  onSubmit: (values: Partial<PaymentInfo>) => unknown;
  onSave: (paymentId: string) => void;
  data: PaymentInfo;
  paymentOverdue?: PaymentInfo;
  renterId: string;
};

export default function FormEditPayments(props: FormPropsEditPayments) {
  const [, setIsSubmitting] = useState(false);
  const [isInterestApplied, setIsInterestApplied] = useState(false);
  const { notifySuccess, notifyError } = useToastContext()


  const { initialValues, paymentOptions } = usePaymentForm({
    data: props.data,
    paymentOverdue: props.paymentOverdue,
    renterId: props.renterId,
  });

  const validationSchema = Yup.object().shape({
    paymentMethod: Yup.string()
      .test('valid-payment-method', 'Selecione um método de pagamento válido', (value) => {
        return value !== '-' && value !== ''; // Garante que "-" e "" não sejam aceitos
      })
      .required('Campo obrigatório'),
    dueDate: Yup.date().when('statusPayment', {
      is: 'OVERDUE',
      then: (schema) => schema.min(truncateDate(new Date()), 'A data de vencimento não pode ser anterior à data atual'),
      otherwise: (schema) => schema, // Sem restrições adicionais
    }),
  });


  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      try {
        setIsSubmitting(true);
        // Remove caracteres especiais e converte para número antes de enviar para o backend
        const cleanValues = {
          ...values,
          isInterestApplied: isInterestApplied,
        };

        const result = await props.onSubmit(cleanValues);
        if (!result) {
          notifyError("Erro ao atualizar fatura");
        } else {
          // Só exibe mensagem de sucesso se result for truthy
          notifySuccess('Fatura atualizada com sucesso!');
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    enableReinitialize: true, // Isso fará com que o Formik atualize os valores se o initialValues mudar
  });

  const originalValueRef = useRef<number>(formik.initialValues.value);

  useEffect(() => {
    if (isInterestApplied) {
      // Altera para o novo valor
      formik.setFieldValue("value", formik.values.newValue);
    } else {
      // Restaura o valor original
      formik.setFieldValue("value", originalValueRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInterestApplied]);


  const depositValue = formik.values.totalValue && formik.values.totalValue !== "R$ 0,00"
    ? formik.values.totalValue
    : formik.values.value;


  // Obtenha a função memorizada para calcular as opções
  const calculateInstallmentOptions = useCalculateInstallmentOptions();

  // Memorize o array de opções de parcelamento
  const installmentOptions: InstallmentOption[] = useMemo(() => {
    return calculateInstallmentOptions(Number(depositValue), 3);
  }, [depositValue, calculateInstallmentOptions]);

  useEffect(() => {
    const valueSelectedOption = installmentOptions.find(
      option => option.value === formik.values.installments
    );
    const installmentAmount = valueSelectedOption?.valueOption || 0;
    if (installmentAmount && Number(formik.values.installmentValue) !== Number(installmentAmount)) {
      formik.setFieldValue('installmentValue', installmentAmount);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.paymentMethod, installmentOptions]);


  return (
    <form onSubmit={formik.handleSubmit}>
      <FormikProvider value={formik}>
        {props.data.status === 'OVERDUE' && (
          <div>
            <h2 className="text-redPrimary">
              Esta fatura está atrasada. A alteração do tipo de pagamento deve incluir o valor total (valor + juros),
              pois o Asaas não permite apenas a troca do tipo de pagamento; é necessário também alterar a data para a atual.
            </h2>
          </div>
        )}
        <div className="grid grid-cols-2 gap-2">
          <div className="col-span-2 bg-gray2/20 mt-5 mb-10 p-3 rounded-md shadow">
            <section className="grid gap-2">
              {props.paymentOverdue ? (
                <div>

                  <InputMoney
                    label="valor da fatura com juros"
                    name="value"
                    required={true}
                  />
                  <div className="pb-3 mt-2" style={{ display: 'flex', alignItems: 'center' }}>
                    <label className="font-semibold text-[14px]"> Aplicar Juros ?  </label>
                    <SlideToggle
                      name="isInterestApplied"
                      className="ml-2"
                      onToggle={(checked) => {
                        setIsInterestApplied(checked);
                        formik.setFieldValue("isInterestApplied", checked);
                      }}
                    />
                    <span style={{ marginLeft: '10px' }}>{isInterestApplied ? "Sim" : "Não"}</span>
                  </div>
                </div>

              ) : (
                <InputMoney
                  label="valor da fatura"
                  name="value"
                  required={true}
                />
              )}
              <InputFormik
                label="Data de vencimento"
                name="dueDate"
                type="date"
                required={true}
                value={formik.values.dueDate}
                onChange={(e) => formik.setFieldValue("dueDate", e.target.value)}
              />
              {/CAUÇÃO/.test(props.data.description!) ? (
                <div>
                  <ComboBoxFormik
                    name="paymentMethod"
                    label="Escolha o método de pagamento:"
                    options={paymentOptions}
                    required={true}
                  />
                  {formik.values.paymentMethod === 'CREDIT_CARD' && (
                    <div>
                      <InputMoney
                        label="Novo valor do cução"
                        name="totalValue"
                        required={true}
                      />
                      <ComboBoxFormik
                        name="installments"
                        label="Escolha a opção de parcelamento:"
                        options={installmentOptions}
                        required={true}
                      />
                    </div>
                  )}
                </div>
              ) : (
                <ComboBoxFormik
                  name="paymentMethod"
                  label="Escolha o método de pagamento:"
                  options={paymentOptions}
                  required={true}
                />
              )}
            </section>
            <div>
              <label className="block text-sm font-medium text-gray-700">Descrição</label>
              <textarea
                required={true}
                name="description"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.description}
                rows={3}
                className="resize-none overflow-hidden block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                style={{ height: 'auto', maxHeight: '300px' }}
                onInput={(e) => {
                  const target = e.target as HTMLTextAreaElement;
                  target.style.height = 'auto';
                  target.style.height = `${target.scrollHeight}px`;
                }}
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <ButtonWithLoader
            isLoading={formik.isSubmitting}
            disabled={!formik.isValid}
            onClick={formik.handleSubmit}>
            Salvar
          </ButtonWithLoader>
        </div>
      </FormikProvider>
    </form>
  );
}
